import { Box, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { MailingTableRow } from "components/mailing-components/mailing-table";
import { StatesMachine } from "../classes/state-machine/state-machine";
import { StateProps } from "../classes/state-machine/state";
import {
  BLOCKS_BY_OPERATION,
  BLOCKS_LOCKED_SUBTITLE,
  BLOCKS_TITLE,
  CATEGORY_ORDER,
  CATEGORY_TITLE,
  getDefaultNodeLabel,
  ORIGIN_HELPER_TEXT,
  PIPELINE_BLOCKS,
  PIPELINE_DRAG_TYPE,
  PipelineBlockCategory
} from "../node-catalog";

export function PipelineBlocksPanel({
  pipelineData,
  isDisabled,
  visibleOperations,
  onInsertOperation,
  mailingData
}: {
  pipelineData?: StatesMachine;
  isDisabled: boolean;
  visibleOperations?: Set<string>;
  onInsertOperation?: (operation: StateProps["operation"]) => void;
  mailingData?: Partial<MailingTableRow>;
}) {
  const groupedByCategory = CATEGORY_ORDER.map((category) => ({
    category,
    blocks: PIPELINE_BLOCKS.filter(
      (item) =>
        item.category === category &&
        (visibleOperations ? visibleOperations.has(item.operation) : true)
    )
  })).filter((group) => group.blocks.length > 0);

  const hasOperation = (operation: string) =>
    pipelineData?.states.some((state) => state.operation === operation) ??
    false;

  return (
    <Flex
      direction="column"
      w="100%"
      h="100%"
      bg="#F7F8FA"
      borderRight="1px solid"
      borderColor="#D6DCE6"
      px="10px"
      py="12px"
      overflow="auto"
      gap="10px"
    >
      <Box>
        <Text
          fontSize="md"
          fontWeight="800"
          letterSpacing="0.08em"
          color="#69778C"
        >
          {BLOCKS_TITLE}
        </Text>
        <Text mt="2px" fontSize="12px" color="#96A2B3" fontWeight="medium">
          {isDisabled ? BLOCKS_LOCKED_SUBTITLE : ORIGIN_HELPER_TEXT}
        </Text>
      </Box>

      {groupedByCategory.map((group) => (
        <BlockCategorySection
          key={group.category}
          category={group.category}
          isDisabled={isDisabled}
          hasOperation={hasOperation}
          operations={group.blocks.map((item) => item.operation)}
          onInsertOperation={onInsertOperation}
          mailingData={mailingData}
        />
      ))}
    </Flex>
  );
}

function BlockCategorySection({
  category,
  isDisabled,
  hasOperation,
  operations,
  onInsertOperation,
  mailingData
}: {
  category: PipelineBlockCategory;
  isDisabled: boolean;
  hasOperation: (operation: StateProps["operation"]) => boolean;
  operations: StateProps["operation"][];
  onInsertOperation?: (operation: StateProps["operation"]) => void;
  mailingData?: Partial<MailingTableRow>;
}) {
  return (
    <Box>
      <Text
        mb="8px"
        fontSize="14px"
        letterSpacing="0.08em"
        fontWeight="800"
        color="#91A0B6"
      >
        {CATEGORY_TITLE[category]}
      </Text>

      <Stack spacing="8px">
        {operations.map((operation) => {
          const item =
            BLOCKS_BY_OPERATION[operation as keyof typeof BLOCKS_BY_OPERATION];
          const isFixed = !!item.fixed;
          const isAlreadyAdded = hasOperation(item.operation);
          const isDragDisabled = isDisabled || isFixed || isAlreadyAdded;
          const blockData = getDefaultNodeLabel(
            item.operation,
            mailingData?.origem,
            mailingData?.qtdGerada
          );
          return (
            <Flex
              key={item.operation}
              align="center"
              gap="8px"
              p="8px"
              borderRadius="16px"
              border="1px solid"
              borderColor={item.accentColor + "55"}
              bg={isDragDisabled ? "#F1F3F6" : "#FFFFFF"}
              cursor={isDragDisabled ? "not-allowed" : "grab"}
              opacity={isDragDisabled && !isFixed ? 0.65 : 1}
              draggable={!isDragDisabled}
              onClick={() => {
                if (isDragDisabled) return;
                onInsertOperation?.(item.operation);
              }}
              onDragStart={(event) => {
                event.dataTransfer.setData(PIPELINE_DRAG_TYPE, item.operation);
                event.dataTransfer.effectAllowed = "move";
              }}
            >
              <Flex
                w="36px"
                h="36px"
                borderRadius="14px"
                align="center"
                justify="center"
                bg={item.softColor}
                color={item.accentColor}
                flexShrink={0}
              >
                <Icon as={item.icon} fontSize="20px" />
              </Flex>

              <Box minW={0}>
                <Text
                  color="#344054"
                  fontSize="13px"
                  fontWeight="800"
                  lineHeight="1.1"
                  mb="4px"
                >
                  {blockData.title}
                </Text>
                <Text
                  color="#8B96A8"
                  fontSize="12px"
                  fontWeight="600"
                  lineHeight="1.1"
                  isTruncated
                >
                  {isAlreadyAdded && !isFixed
                    ? "Bloco já adicionado"
                    : blockData.subtitle}
                </Text>
              </Box>
            </Flex>
          );
        })}
      </Stack>
    </Box>
  );
}
